import { computed, ref, watch } from 'vue';
import { useTracking } from 'dashboard/composables';
import { LAIS_TRACKING_EVENTS } from 'dashboard/helper/lais/trackingEvents';
import {
  compactQuery,
  numericValue,
  queryValue,
} from '../utils/qprecoFormatters';

/**
 * Abertura, rastreio e fechamento do relatório de preço (PricingReportModal),
 * incluindo o deeplink `?showPricingReportQuery=true&section=...`.
 *
 * Extraído de `pages/AreaQprecoIndex.vue` mantendo a família de eventos
 * `pricing_report_*` e a limpeza da query ao fechar.
 */
export function useQprecoPricingReport({
  route,
  router,
  accountId,
  activeInterestId,
  detailInterest,
}) {
  const { track } = useTracking();

  const showPricingReport = ref(false);
  const activeReportInterestId = ref('');
  const activeReportContext = ref(null);
  const pricingReportEntrypoint = ref('PRICE_ACTIONS');
  const pricingReportInitialSection = ref('');
  const pricingReportOpenedAt = ref(0);
  const consumedPricingReportQueryKey = ref('');

  const pricingReportContext = computed(
    () => activeReportContext.value || detailInterest.value || {}
  );
  const pricingReportTitle = computed(() => {
    const context = pricingReportContext.value;
    const type = context?.tipo || context?.type || 'Imóvel';
    const neighborhood = context?.bairro || context?.neighborhood;
    return neighborhood
      ? `Relatório de preço — ${type} · ${neighborhood}`
      : `Relatório de preço — ${type}`;
  });
  const pricingReportSubtitle = computed(() => {
    const context = pricingReportContext.value;
    return (
      context?.addressDetails ||
      context?.imovel_endereco ||
      context?.address ||
      context?.endereco ||
      [context?.bairro, context?.cidade, context?.uf]
        .filter(Boolean)
        .join(', ') ||
      'Endereço não informado'
    );
  });
  const isPricingReportLoading = interestId =>
    showPricingReport.value &&
    String(activeReportInterestId.value) === String(interestId);
  const isPricingReportUnavailable = item =>
    item?.confidence === 'insufficient_data' ||
    item?.confidence_level === 'insufficient_data';

  const pricingReportTrackingBase = (extras = {}) => ({
    accountId: accountId.value,
    interest_id: activeReportInterestId.value || activeInterestId.value,
    entrypoint: pricingReportEntrypoint.value,
    publication_type:
      extras.publication_type || pricingReportContext.value?.finalidade,
  });

  const openPricingReport = ({
    interestId,
    item = null,
    entrypoint = 'PRICE_ACTIONS',
    initialSection = '',
  } = {}) => {
    const reportInterestId = interestId || activeInterestId.value;
    if (!reportInterestId) return;

    activeReportInterestId.value = String(reportInterestId);
    activeReportContext.value =
      item || (detailInterest.value?.id ? detailInterest.value : null);
    pricingReportEntrypoint.value = entrypoint;
    pricingReportInitialSection.value = initialSection;
    pricingReportOpenedAt.value = Date.now();
    showPricingReport.value = true;
  };

  const handlePricingReportLinkClick = (payload, item = null) => {
    track(LAIS_TRACKING_EVENTS.PRICING_REPORT_LINK_CLICKED, {
      ...pricingReportTrackingBase({
        publication_type: payload?.publicationType,
      }),
      interest_id: payload?.interestId,
      entrypoint: payload?.entrypoint,
    });
    openPricingReport({
      interestId: payload?.interestId,
      item,
      entrypoint: payload?.entrypoint,
    });
  };

  const handlePricingReportLoaded = reportPayload => {
    track(LAIS_TRACKING_EVENTS.PRICING_REPORT_OPENED, {
      ...pricingReportTrackingBase({
        publication_type: reportPayload?.publication_type,
      }),
      confidence_level: reportPayload?.confidence_level,
      comparables_count: numericValue(reportPayload?.comparables_count),
    });
  };

  const handlePricingReportSectionViewed = section => {
    track(LAIS_TRACKING_EVENTS.PRICING_REPORT_SECTION_VIEWED, {
      ...pricingReportTrackingBase(),
      section,
    });
  };

  const handlePricingReportCtaClicked = payload => {
    track(LAIS_TRACKING_EVENTS.PRICING_REPORT_CTA_CLICKED, {
      ...pricingReportTrackingBase(),
      cta: payload?.cta,
    });
  };

  const cleanPricingReportQuery = () => {
    if (!route.query.showPricingReportQuery && !route.query.section) return;

    const nextQuery = { ...route.query };
    delete nextQuery.showPricingReportQuery;
    delete nextQuery.section;
    router.replace({
      name: route.name,
      params: route.params,
      query: compactQuery(nextQuery),
    });
  };

  const closePricingReport = () => {
    if (showPricingReport.value) {
      track(LAIS_TRACKING_EVENTS.PRICING_REPORT_CLOSED, {
        ...pricingReportTrackingBase(),
        duration_ms: pricingReportOpenedAt.value
          ? Date.now() - pricingReportOpenedAt.value
          : undefined,
      });
    }

    showPricingReport.value = false;
    cleanPricingReportQuery();
  };

  const openPricingReportFromQuery = () => {
    if (
      route.query.showPricingReportQuery !== 'true' ||
      !activeInterestId.value
    ) {
      if (route.query.showPricingReportQuery !== 'true') {
        consumedPricingReportQueryKey.value = '';
      }
      return;
    }

    const queryKey = `${activeInterestId.value}:${route.query.section || ''}`;
    if (consumedPricingReportQueryKey.value === queryKey) return;

    consumedPricingReportQueryKey.value = queryKey;
    openPricingReport({
      interestId: activeInterestId.value,
      item: detailInterest.value,
      entrypoint: 'deeplink',
      initialSection: queryValue(route.query.section),
    });
  };

  watch(
    [
      activeInterestId,
      () => route.query.showPricingReportQuery,
      () => route.query.section,
    ],
    openPricingReportFromQuery,
    { immediate: true }
  );

  return {
    showPricingReport,
    activeReportInterestId,
    pricingReportInitialSection,
    pricingReportTitle,
    pricingReportSubtitle,
    isPricingReportLoading,
    isPricingReportUnavailable,
    handlePricingReportLinkClick,
    handlePricingReportLoaded,
    handlePricingReportSectionViewed,
    handlePricingReportCtaClicked,
    closePricingReport,
  };
}
